// client/src/components/itensVenda/ItemVendaImages.js - VERSÃO CORRIGIDA
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useMutateItensVenda } from '../../hooks/useItensVenda';
import useAlert from '../../hooks/useAlert';
import { UploadCloud, Trash2, Loader2, Search as SearchIcon, XCircle, Star, Image as ImageIcon, Info } from 'lucide-react';
import GaleriaBuscaModal from '../galeria/GaleriaBuscaModal';
import PropTypes from 'prop-types';
import GerarImagemIABox from '../ai/GerarImagemIABox';

const ItemVendaImages = ({ item, onUpdate }) => {
    const { setAlert } = useAlert();
    const { 
        updateItemVenda, uploadItemVendaImage, deleteItemVendaImage,
        setItemVendaImagemPrincipal, isPending: loading
    } = useMutateItensVenda();

    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [showGalleryModal, setShowGalleryModal] = useState(false);
    
    // ✅ CORREÇÃO: Estado local para controle visual IMEDIATO
    const [imagemPrincipalLocal, setImagemPrincipalLocal] = useState(null);
    const [imagensLocais, setImagensLocais] = useState({ custom: [], galeria: [] });

    // ✅ CORREÇÃO: Inicializar estados locais quando o item mudar
    useEffect(() => {
        if (item) {
            console.log('🔄 [ItemVendaImages] Inicializando estados locais com item:', item.nome);
            setImagemPrincipalLocal(item.imagemPrincipal || null);
            setImagensLocais({
                custom: item.imagens || [],
                galeria: item.imagensDaGaleria || []
            });
        }
    }, [item]);

    // ✅ CORREÇÃO: safeItem usa estados locais para dados visuais
    const safeItem = useMemo(() => {
        if (!item) {
            return {
                _id: null,
                nome: 'Carregando...',
                imagens: [],
                imagensDaGaleria: [],
                imagemPrincipal: null
            };
        }
        
        // ✅ CORREÇÃO: Usar estados locais para dados visuais
        const itemComDadosLocais = {
            ...item,
            imagens: imagensLocais.custom,
            imagensDaGaleria: imagensLocais.galeria,
            imagemPrincipal: imagemPrincipalLocal || item.imagemPrincipal || null
        };

        console.log('🎯 [ItemVendaImages] safeItem com dados locais:', {
            nome: itemComDadosLocais.nome,
            imagensCount: itemComDadosLocais.imagens.length,
            galeriaCount: itemComDadosLocais.imagensDaGaleria.length, // ✅ CORREÇÃO: itemComDadosLocais
            imagemPrincipal: itemComDadosLocais.imagemPrincipal
        });

        return itemComDadosLocais;
    }, [item, imagensLocais, imagemPrincipalLocal]);

    const itemId = safeItem._id;

    // ✅ CORREÇÃO: Função de processamento de imagens
    const processarImagens = useCallback((imagensArray, tipo) => {
        if (!imagensArray || !Array.isArray(imagensArray)) {
            return [];
        }
        
        const imagensProcessadas = imagensArray.map((img, index) => {
            if (!img || typeof img !== 'object') {
                return null;
            }

            const urlOriginal = img.url || img.arquivo || img.path || img.caminhoS3 || img.imagemUrl || img.src;
            
            if (!urlOriginal) {
                return null;
            }

            return {
                _id: img._id || img.id || `temp-${tipo}-${index}`,
                url: urlOriginal,
                urlOriginal: urlOriginal,
                nomeOriginal: img.nomeOriginal || img.originalname || img.nome || `Imagem ${index + 1}`,
                tipo: tipo,
                principal: false
            };
        }).filter(img => img !== null);
        
        return imagensProcessadas;
    }, []);

    // ✅ CORREÇÃO: Processamento de todas as imagens usando dados locais
    const todasImagens = useMemo(() => {
        if (!safeItem) return [];

        const imagensCustom = processarImagens(safeItem.imagens, 'custom');
        const imagensGaleria = processarImagens(safeItem.imagensDaGaleria, 'galeria');
        
        const todas = [...imagensGaleria, ...imagensCustom];
        
        // ✅ CORREÇÃO: Marcar imagem principal usando estado local
        const principalRefId = safeItem.imagemPrincipal?.refId?.toString();
        if (principalRefId) {
            console.log('⭐ [ItemVendaImages] Marcando imagem principal:', principalRefId);
            todas.forEach(img => {
                if (img._id && img._id.toString() === principalRefId) {
                    img.principal = true;
                }
            });
        }
        
        console.log('📊 [ItemVendaImages] Imagens processadas:', {
            total: todas.length,
            principais: todas.filter(img => img.principal).length,
            principalRefId: principalRefId
        });
        
        return todas;
    }, [safeItem, processarImagens]);

    // ✅ CORREÇÃO: Função para upload de imagem - ATUALIZAÇÃO IMEDIATA
    const handleImageUpload = () => {
        if (!itemId || !imageFile) return;

        console.log('📤 [ItemVendaImages] Iniciando upload para item:', itemId);

        // ✅ CORREÇÃO: Criar objeto temporário para preview imediato
        const tempImage = {
            _id: `temp-upload-${Date.now()}`,
            url: imagePreview,
            urlOriginal: imagePreview,
            nomeOriginal: imageFile.name,
            tipo: 'custom',
            principal: false,
            isTemp: true // Marcar como temporária
        };

        // ✅ CORREÇÃO: Atualização IMEDIATA do estado local ANTES da requisição
        setImagensLocais(prev => ({
            ...prev,
            custom: [...prev.custom, tempImage]
        }));

        console.log('🎯 [ItemVendaImages] Estado local atualizado IMEDIATAMENTE com imagem temporária');

        uploadItemVendaImage.mutate({ itemId: itemId, file: imageFile }, {
            onSuccess: (response) => {
                console.log('✅ [ItemVendaImages] Upload bem-sucedido:', response);
                
                // ✅ CORREÇÃO: Limpar estados de upload
                setImageFile(null);
                setImagePreview(null);
                const input = document.getElementById('item-image-upload-component');
                if (input) input.value = '';
                
                // ✅ CORREÇÃO: Não chamar onUpdate para evitar loop
                // A atualização visual já foi feita via estado local
                // O React Query irá invalidar o cache e atualizar os dados
                
                setAlert('Imagem adicionada com sucesso!', 'success');
            },
            onError: (error) => {
                console.error('❌ [ItemVendaImages] Erro no upload:', error);
                
                // ✅ CORREÇÃO: Reverter estado local em caso de erro
                setImagensLocais(prev => ({
                    ...prev,
                    custom: prev.custom.filter(img => !img.isTemp) // Remove apenas a imagem temporária
                }));
                
                setAlert(error.response?.data?.error || 'Erro ao fazer upload da imagem', 'danger');
            }
        });
    };

    // ✅ CORREÇÃO: Função para definir imagem principal
    const handleSetMainImage = (tipo, refId) => {
        if (!itemId || !refId) return;

        const currentPrincipalRefId = safeItem.imagemPrincipal?.refId?.toString();
        if (currentPrincipalRefId === refId.toString()) {
            return setAlert('Esta imagem já é a principal.', 'info');
        }

        console.log('⭐ [ItemVendaImages] Definindo imagem principal:', { 
            itemId, 
            tipo, 
            refId,
            currentPrincipal: safeItem.imagemPrincipal 
        });

        // ✅ CORREÇÃO: Atualização IMEDIATA do estado local
        const novaImagemPrincipal = { 
            refId: refId.toString(), 
            tipo: tipo 
        };
        
        setImagemPrincipalLocal(novaImagemPrincipal);
        console.log('🎯 [ItemVendaImages] Estado local atualizado IMEDIATAMENTE:', novaImagemPrincipal);

        setItemVendaImagemPrincipal.mutate({ itemId: itemId, tipo, refId }, {
            onSuccess: (response) => {
                console.log('✅ [ItemVendaImages] Imagem principal definida com sucesso no servidor');
                
                // ✅ CORREÇÃO: Não chamar onUpdate para evitar loop
                // A atualização já foi feita via estado local
                // O React Query irá invalidar o cache e atualizar os dados
                
                setAlert('Imagem principal definida com sucesso!', 'success');
            },
            onError: (error) => {
                console.error('❌ [ItemVendaImages] Erro ao definir imagem principal:', error);
                
                // ✅ CORREÇÃO: Reverter estado local em caso de erro
                setImagemPrincipalLocal(item?.imagemPrincipal || null);
                
                setAlert(error.response?.data?.error || 'Erro ao definir imagem principal', 'danger');
            }
        });
    };

    // ✅ CORREÇÃO: Função para remover imagem customizada
    const handleCustomImageDelete = (imagemId) => {
        if (!itemId || !imagemId) return;

        if (window.confirm('Tem certeza que deseja remover esta imagem customizada?')) {
            console.log('🗑️ [ItemVendaImages] Removendo imagem customizada:', imagemId);
            
            // ✅ CORREÇÃO: Atualização IMEDIATA do estado local ANTES da requisição
            const novasImagensCustom = safeItem.imagens.filter(img => {
                const imgId = img._id || img.id;
                return imgId && imgId.toString() !== imagemId.toString();
            });
            
            setImagensLocais(prev => ({
                ...prev,
                custom: novasImagensCustom
            }));
            
            // ✅ CORREÇÃO: Se a imagem removida era a principal, limpar estado local
            if (safeItem.imagemPrincipal?.refId?.toString() === imagemId.toString()) {
                setImagemPrincipalLocal(null);
                console.log('🔄 [ItemVendaImages] Removendo imagem principal do estado local');
            }
            
            deleteItemVendaImage.mutate({ itemId: itemId, imagemId: imagemId }, {
                onSuccess: (response) => {
                    console.log('✅ [ItemVendaImages] Imagem removida com sucesso');
                    
                    // ✅ CORREÇÃO: Não chamar onUpdate para evitar loop
                    // A atualização visual já foi feita via estado local
                    // O React Query irá invalidar o cache e atualizar os dados
                    
                    setAlert('Imagem removida com sucesso!', 'success');
                },
                onError: (error) => {
                    console.error('❌ [ItemVendaImages] Erro ao remover imagem:', error);
                    
                    // ✅ CORREÇÃO: Reverter estado local em caso de erro
                    setImagensLocais({
                        custom: item?.imagens || [],
                        galeria: item?.imagensDaGaleria || []
                    });
                    setImagemPrincipalLocal(item?.imagemPrincipal || null);
                    
                    setAlert(error.response?.data?.error || 'Erro ao remover imagem', 'danger');
                }
            });
        }
    };

    // ✅ CORREÇÃO: Função para desvincular imagem da galeria
    const handleGalleryImageRemove = (imagemId) => {
        if (!itemId || !imagemId) return;

        if (window.confirm('Tem certeza que deseja desvincular esta imagem da galeria?')) {
            console.log('🔗 [ItemVendaImages] Desvinculando imagem da galeria:', imagemId);
            
            // ✅ CORREÇÃO: Atualização IMEDIATA do estado local ANTES da requisição
            const novasImagensGaleria = safeItem.imagensDaGaleria.filter(img => {
                const imgId = img._id || img.id || img;
                return imgId && imgId.toString() !== imagemId.toString();
            });
            
            setImagensLocais(prev => ({
                ...prev,
                galeria: novasImagensGaleria
            }));
            
            let payload = { imagensDaGaleria: novasImagensGaleria.map(img => img._id || img.id || img) };
            
            // ✅ CORREÇÃO: Se a imagem removida era a principal, limpar estado local
            if (safeItem.imagemPrincipal?.refId?.toString() === imagemId.toString()) {
                payload.imagemPrincipal = null;
                setImagemPrincipalLocal(null);
                console.log('🔄 [ItemVendaImages] Removendo imagem principal do estado local');
            }

            updateItemVenda.mutate({ id: itemId, itemData: payload }, {
                onSuccess: (response) => {
                    console.log('✅ [ItemVendaImages] Imagem desvinculada com sucesso');
                    
                    // ✅ CORREÇÃO: Não chamar onUpdate para evitar loop
                    // A atualização visual já foi feita via estado local
                    
                    setAlert('Imagem desvinculada com sucesso!', 'success');
                },
                onError: (error) => {
                    console.error('❌ [ItemVendaImages] Erro ao desvincular imagem:', error);
                    
                    // ✅ CORREÇÃO: Reverter estado local em caso de erro
                    setImagensLocais({
                        custom: item?.imagens || [],
                        galeria: item?.imagensDaGaleria || []
                    });
                    setImagemPrincipalLocal(item?.imagemPrincipal || null);
                    
                    setAlert(error.response?.data?.error || 'Erro ao desvincular imagem', 'danger');
                }
            });
        }
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (!file.type.startsWith('image/')) {
                setAlert('Por favor, selecione apenas arquivos de imagem.', 'danger');
                return;
            }

            if (file.size > 5 * 1024 * 1024) {
                setAlert('A imagem deve ter no máximo 5MB.', 'danger');
                return;
            }

            setImageFile(file);
            const previewUrl = URL.createObjectURL(file);
            setImagePreview(previewUrl);
        }
    };

    const handleGalleryImageSelect = (imagemSelecionada) => {
        if (!itemId) return;

        const isAlreadyLinked = (safeItem.imagensDaGaleria || []).some(img => {
            const imgId = img._id || img.id || img;
            return imgId && imgId.toString() === imagemSelecionada._id.toString();
        });
        
        if (isAlreadyLinked) {
            return setAlert('Esta imagem já está no item.', 'info');
        }
        
        const novosIds = [...(safeItem.imagensDaGaleria || []).map(img => img._id || img.id || img), imagemSelecionada._id];

        updateItemVenda.mutate({ id: itemId, itemData: { imagensDaGaleria: novosIds } }, {
            onSuccess: (response) => {
                console.log('✅ [ItemVendaImages] Imagem vinculada com sucesso');
                setShowGalleryModal(false);
                
                // ✅ CORREÇÃO: Para vinculação de galeria, podemos chamar onUpdate
                // pois é uma operação menos frequente e não causa problemas de performance
                const itemAtualizadoDaApi = response.data || response.item || response;
                if (itemAtualizadoDaApi && onUpdate) {
                    onUpdate(itemAtualizadoDaApi);
                }
                
                setAlert('Imagem vinculada com sucesso!', 'success');
            },
            onError: (error) => {
                console.error('❌ [ItemVendaImages] Erro ao vincular imagem:', error);
                setAlert(error.response?.data?.error || 'Erro ao vincular imagem', 'danger');
            }
        });
    };

    // ✅ CORREÇÃO: Função isPrincipal usando estado local
    const isPrincipal = (img) => {
        const principalRefId = safeItem.imagemPrincipal?.refId?.toString();
        const imgId = img._id?.toString();
        
        const resultado = principalRefId && imgId && principalRefId === imgId;
        
        return resultado;
    };

    const placeholderImage = 'https://via.placeholder.com/150?text=Sem+Imagem';

    // ✅ CORREÇÃO: Verificação se pode editar
    const canEditImages = itemId && itemId !== 'unknown-id' && itemId !== 'temp-';

    if (!canEditImages) {
        return (
            <fieldset className="form-section">
                <legend className="form-section-title">Imagens do Item</legend>
                <div className="panel-state-wrapper panel-info">
                    <Info size={24} className="icon"/>
                    <span>Primeiro, salve o item. Após salvar, as opções de gerenciamento de imagens aparecerão aqui.</span>
                </div>
            </fieldset>
        );
    }

    if (!safeItem._id) {
        return (
            <fieldset className="form-section">
                <legend className="form-section-title">Imagens do Item</legend>
                <div className="panel-state-wrapper">
                    <Loader2 size={24} className="animate-spin"/>
                    <span>Carregando imagens...</span>
                </div>
            </fieldset>
        );
    }

    return (
        <fieldset className="form-section" disabled={loading}>
            <legend className="form-section-title">Imagens do Item: {safeItem.nome}</legend>
            
            {/* Buscar na Galeria Global */}
            <div className="indented-panel">
                <h4 className="panel-subtitle">Buscar na Galeria Global</h4>
                <p className="form-hint mb-2">Encontre e vincule imagens da galeria global de imagens</p>
                <button 
                    type="button" 
                    onClick={() => setShowGalleryModal(true)} 
                    className="action-button action-button-secondary mt-2"
                    disabled={loading}
                >
                    <SearchIcon size={16} className="icon" /> Buscar na Galeria
                </button>
            </div>

            {/* Adicionar Nova Imagem Customizada */}
            <div className="indented-panel mt-4">
                <h4 className="panel-subtitle">Adicionar Nova Imagem Customizada</h4>
                <p className="form-hint mb-2">Faça upload de uma nova imagem específica para este produto</p>
                <div className="flex items-center gap-4 flex-wrap mt-2">
                    <div className="w-24 h-24 border-2 border-dashed rounded-lg flex items-center justify-center bg-gray-50">
                        {imagePreview ? 
                            <img 
                                src={imagePreview} 
                                alt="Preview" 
                                className="w-full h-full object-cover rounded-md"
                                onError={(e) => {
                                    e.target.src = placeholderImage;
                                }}
                            /> : 
                            <ImageIcon size={32} className="text-gray-400" />
                        }
                    </div>
                    <div className="form-group flex-1 min-w-[250px]">
                        <label htmlFor="item-image-upload-component">Escolher arquivo de imagem</label>
                        <input 
                            type="file" 
                            id="item-image-upload-component" 
                            onChange={handleFileChange} 
                            accept="image/*" 
                            className="form-input" 
                            disabled={loading}
                        />
                        <div className="form-hint">Formatos: JPG, PNG, GIF, WebP • Máx: 5MB</div>
                    </div>
                    <button 
                        type="button" 
                        onClick={handleImageUpload} 
                        disabled={!imageFile || loading} 
                        className="action-button action-button-primary"
                    >
                        {loading ? <Loader2 className="animate-spin icon"/> : <UploadCloud size={18} className="icon" />}
                        {loading ? 'Enviando...' : 'Enviar Imagem'}
                    </button>
                </div>

                <GerarImagemIABox
                    aspectRatio="3:2"
                    aspectHint="Tamanho ideal: 800×600px, paisagem. Evite fotos na vertical."
                    placeholder='Ex: "foto de produto em fundo neutro, iluminação profissional de estúdio"'
                    onUseImage={(file, previewUrl) => { setImageFile(file); setImagePreview(previewUrl); }}
                    itemContexto={{ tipo: 'itemVenda', nome: safeItem?.nome, descricao: safeItem?.descricao, categoria: safeItem?.categoria?.nome }}
                />
            </div>

            {/* Galeria de Imagens do Item */}
            <div className="mt-6">
                <h4 className="panel-subtitle">
                    Galeria de Imagens do Item 
                    <span className="ml-2 text-sm font-normal text-gray-700 bg-gray-100 px-2 py-1 rounded-full">
                        {todasImagens.length} {todasImagens.length === 1 ? 'imagem' : 'imagens'}
                    </span>
                    {todasImagens.length > 0 && (
                        <span className="ml-2 text-sm font-normal text-amber-600 bg-amber-100 px-2 py-1 rounded-full">
                            {todasImagens.filter(img => isPrincipal(img)).length} principal
                        </span>
                    )}
                </h4>
                
                {todasImagens.length > 0 ? (
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 mt-4">
                        {todasImagens.map((img) => (
                            <div key={img._id} className="relative group border rounded-lg overflow-hidden shadow-sm aspect-square bg-white hover:shadow-md transition-shadow">
                                <img 
                                    src={img.url} 
                                    alt={img.nomeOriginal} 
                                    className="w-full h-full object-cover"
                                    onError={(e) => {
                                        e.target.src = placeholderImage;
                                    }}
                                />
                                
                                {/* ✅ BADGE PRINCIPAL - AGORA MANTÉM A ATUALIZAÇÃO */}
                                {isPrincipal(img) && (
                                    <div className="absolute top-1 right-1 bg-amber-400 text-white p-1 rounded-full z-10 shadow-sm">
                                        <Star size={14} fill="white" />
                                    </div>
                                )}
                                
                                {/* Badge do tipo */}
                                <div className={`absolute top-1 left-1 text-white text-xs px-1 py-0.5 rounded z-10 shadow-sm ${
                                    img.tipo === 'galeria' ? 'bg-blue-500' : 'bg-green-500'
                                }`}>
                                    {img.tipo === 'galeria' ? 'Galeria' : 'Custom'}
                                </div>

                                {/* Overlay com ações */}
                                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-60 transition-all flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
                                    <button 
                                        type="button"
                                        onClick={() => handleSetMainImage(img.tipo, img._id)}
                                        disabled={isPrincipal(img)}
                                        title={isPrincipal(img) ? "Já é a imagem principal" : "Definir como principal"}
                                        className="p-2 bg-white rounded-full text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
                                    >
                                        <Star size={18} fill={isPrincipal(img) ? "currentColor" : "none"} />
                                    </button>
                                    
                                    {img.tipo === 'galeria' ? (
                                        <button 
                                            type="button"
                                            onClick={() => handleGalleryImageRemove(img._id)}
                                            title="Desvincular Imagem"
                                            className="p-2 bg-yellow-500 text-white rounded-full hover:bg-yellow-600 transition-colors shadow-sm"
                                        >
                                            <XCircle size={18} />
                                        </button>
                                    ) : (
                                        <button 
                                            type="button"
                                            onClick={() => handleCustomImageDelete(img._id)}
                                            title="Remover Imagem"
                                            className="p-2 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors shadow-sm"
                                        >
                                            <Trash2 size={18} />
                                        </button>
                                    )}
                                </div>

                                {/* Nome da imagem no hover */}
                                <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-70 text-white text-xs p-1 truncate opacity-0 group-hover:opacity-100 transition-opacity">
                                    {img.nomeOriginal}
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="panel-state-wrapper panel-info mt-4">
                        <ImageIcon size={24} className="icon"/>
                        <span>Este item ainda não possui imagens. Adicione imagens usando as opções acima.</span>
                    </div>
                )}
            </div>

            {/* Modal da Galeria */}
            {showGalleryModal && (
                <GaleriaBuscaModal 
                    servico={safeItem} 
                    onClose={() => setShowGalleryModal(false)}
                    onImageSelect={handleGalleryImageSelect}
                />
            )}

            {/* Informações de ajuda */}
            <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <h5 className="font-medium text-blue-900 mb-2 flex items-center">
                    <Info size={16} className="mr-2" />
                    💡 Dicas para gerenciamento de imagens
                </h5>
                <ul className="text-sm text-blue-700 space-y-1">
                    <li>• <strong>Imagem Principal:</strong> Será exibida como capa do produto</li>
                    <li>• <strong>Imagens da Galeria:</strong> Compartilhadas entre produtos, apenas vinculadas</li>
                    <li>• <strong>Imagens Custom:</strong> Específicas deste produto, armazenadas separadamente</li>
                    <li>• <strong>Atualização:</strong> As mudanças são aplicadas instantaneamente</li>
                </ul>
            </div>
        </fieldset>
    );
};

ItemVendaImages.propTypes = {
    item: PropTypes.object.isRequired,
    onUpdate: PropTypes.func.isRequired
};

export default ItemVendaImages;